import {
  BIOMETRIC_FACE,
  BIOMETRIC_FACE_ID,
  BIOMETRIC_FINGERPRINT,
  BIOMETRIC_IRIS,
  BIOMETRIC_TOUCH_ID,
  LOGIN_TYPE,
} from '/enum/biometrics.enum';
import {withTranslation} from '/provider/TranslationProvider';
import {getBiometricsUtilDefaultInstance} from '/util/biometrics/biometrics.util';
import {generateCallback} from '/util/component.util';
import {validateEmail} from '/util/validation.util';
const errorMessage = 'Ensure a valid email format ex. ‘username@domain.com’.';

export const handleOnLoginChange =
  ({clearErrors, setValue, setEmailError}: any) =>
  (value: any) => {
    clearErrors();
    setValue('login', value);
    if (value === '') {
      setEmailError('');
      return;
    }
    if (!validateEmail(value)) {
      setEmailError(errorMessage);
    } else {
      setEmailError('');
    }
  };

export const handlePasswordChange =
  ({clearErrors, setValue}: any) =>
  (value: any) => {
    clearErrors();
    setValue('password', value);
  };

export const handleBiometricAuth =
  ({promptMessage, setValue, handleOnSubmit}: any) =>
  () => {
    return getBiometricsUtilDefaultInstance()
      .retrievePassword({
        setValue,
        promptMessage,
        loginType: LOGIN_TYPE.biometrics,
      })()
      .then((res: any) => {
        if (res === 'success') {
          generateCallback({func: handleOnSubmit})();
        }
      })
      .catch((err: any) => {
        return err;
      });
  };

interface ButtonData {
  promptMessage: string;
  iconName: string;
  title: string;
}

export const getButtonData =
  ({biometricType}: {biometricType: string}) =>
  (): ButtonData | null => {
    let buttonData: ButtonData | null = null;

    switch (biometricType) {
      case BIOMETRIC_FACE_ID:
        buttonData = {
          promptMessage: withTranslation.ts(
            'biometric.faceId.promptMessage',
            'Confirm Face ID',
          ),
          iconName: 'faceID',
          title: withTranslation.ts('biometric.faceId.title', 'FACE ID'),
        };
        break;

      case BIOMETRIC_TOUCH_ID:
        buttonData = {
          promptMessage: withTranslation.ts(
            'biometric.touchId.promptMessage',
            'Confirm Touch ID',
          ),
          iconName: 'touchID',
          title: withTranslation.ts('biometric.touchId.title', 'TOUCH ID'),
        };
        break;

      case BIOMETRIC_FINGERPRINT:
        buttonData = {
          promptMessage: withTranslation.ts(
            'biometric.biometrics.promptMessage',
            'Finger Recognition',
          ),
          iconName: 'touchID',
          title: withTranslation.ts(
            'biometric.biometrics.title',
            'Finger Recognition',
          ),
        };
        break;

      case BIOMETRIC_FACE:
        buttonData = {
          promptMessage: withTranslation.ts(
            'biometric.biometrics.promptMessage',
            'Face Recognition',
          ),
          iconName: 'faceID',
          title: withTranslation.ts(
            'biometric.biometrics.title',
            'Face Recognition',
          ),
        };
        break;

      case BIOMETRIC_IRIS:
        buttonData = {
          promptMessage: withTranslation.ts(
            'biometric.biometrics.promptMessage',
            'Iris Unlock',
          ),
          iconName: 'faceID',
          title: withTranslation.ts(
            'biometric.irisRecognition.title',
            'Iris Unlock',
          ),
        };
        break;

      default:
        buttonData = null;
    }

    return buttonData;
  };

export const handleBiometricsPrompt = ({
  biometricsOptIn,
  buttonData,
  setValue,
  handleOnSubmit,
  showPopup,
  hidePopup,
}: any) => {
  biometricsOptIn &&
    handleBiometricAuth({
      promptMessage: buttonData.promptMessage,
      setValue,
      handleOnSubmit,
      showPopup,
      hidePopup,
    })();
};

export const handleAutoBiometricsLogin = ({
  shouldAutoLogin,
  biometricsOptIn,
  buttonData,
  setValue,
  handleOnSubmit,
  showPopup,
  hidePopup,
}: any) => {
  shouldAutoLogin &&
    handleBiometricsPrompt({
      biometricsOptIn,
      buttonData,
      setValue,
      handleOnSubmit,
      showPopup,
      hidePopup,
    });
};
export const handleLoginFormErrorMessage = ({
  fieldErrors,
  formError,
  emailError,
}: any) => {
  let errorMessage = '';
  if (
    Boolean(fieldErrors.password) &&
    Boolean(fieldErrors.login) &&
    emailError
  ) {
    // Case: Only login has an error
    errorMessage = withTranslation.ts(
      'loginForm.usernameError',
      'Please enter a username',
    );
  } else if (emailError) {
    // Case: Email-specific error
    errorMessage = withTranslation.ts(
      'loginForm.usernameError',
      'Incorrect email',
    );
  } else if (fieldErrors?.password && !fieldErrors?.login) {
    // Case: Only password has an error
    errorMessage = withTranslation.ts(
      'loginForm.passwordError',
      'Please enter a password',
    );
  } else if (fieldErrors?.login && fieldErrors?.password) {
    // Case: Both login and password have errors
    errorMessage = withTranslation.ts(
      'loginForm.usernameAndPasswordError',
      'Please enter a username and password',
    );
  } else if (formError) {
    // Case: Generic form error
    errorMessage = withTranslation.ts('loginForm.error.', formError);
  }

  return errorMessage;
};
